import { DialogTitle } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import { PaymentCardInfo } from '../payment-card-info';
import * as Styled from './styles';
import type { PaymentConfirmDialogProps as Props } from './types';

const VARIANT_CONFIG = {
  setDefault: {
    title: 'Update payment method',
    body: (
      <Typography variant="body3" color="grey.700">
        Your <strong>new card</strong> will replace your{' '}
        <strong>current card</strong> as your default payment method and as the
        card billed for your current subscription. The change takes effect on
        your next billing cycle.
      </Typography>
    ),
    confirmLabel: 'Update Card',
    confirmColor: 'primary' as const,
  },
  deleteWithDefaultFallback: {
    title: 'Change default card',
    body: (
      <Typography variant="body3" color="grey.700">
        This card is currently used for your subscription. Deleting it will move
        future charges to your <strong>Default</strong> card.
      </Typography>
    ),
    confirmLabel: 'Delete Card',
    confirmColor: 'error' as const,
  },
  deleteGeneric: {
    title: 'Delete Card',
    body: null,
    confirmLabel: 'Yes, Delete',
    confirmColor: 'error' as const,
  },
};

export const PaymentConfirmDialog = memo(
  ({
    className,
    open,
    variant,
    loading,
    card,
    onConfirm,
    onClose,
    iconType,
  }: Props) => {
    const config = VARIANT_CONFIG[variant];

    return (
      <Styled.Wrapper
        className={classnames('PaymentConfirmDialog', className)}
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          title={config.title}
          icon="credit-card-outlined"
          onClose={onClose}
        />

        <DialogContent>
          {variant === 'deleteGeneric' ? (
            <Card variant="outlined">
              <CardContent padding="extra">
                <Stack gap={1.5}>
                  <Typography variant="body2" fontWeight={700} color="grey.700">
                    Are you sure you want to delete this card?
                  </Typography>
                  {card && <PaymentCardInfo item={card} iconType={iconType} />}
                </Stack>
              </CardContent>
            </Card>
          ) : (
            config.body
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="text" size="small" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color={config.confirmColor}
            size="small"
            loading={loading}
            onClick={onConfirm}
          >
            {config.confirmLabel}
          </LoadingButton>
        </DialogActions>
      </Styled.Wrapper>
    );
  }
);

PaymentConfirmDialog.displayName = 'PaymentConfirmDialog';
